import { inject, signal } from '@angular/core';
import { patchState, signalStore, withMethods, withProps, withState } from '@ngrx/signals';

import { RetailerPhoneNumbersApi } from '@core/api';
import * as Entities from '@shared/entities';
import { extractErrorMessage } from '@shared/utils';

export const RetailerPhoneNumbersStore = signalStore(
  { providedIn: 'root' },
  withState({
    records: [] as Entities.RetailerPhoneNumber[],
    loadedRetailerId: null as number | null,
    loading: false,
    errorMessage: null as string | null,
  }),
  withProps(() => {
    const pendingPhoneNumberIds = signal<number[]>([]);
    return {
      pendingPhoneNumberIds: pendingPhoneNumberIds.asReadonly(),
      pendingPhoneNumberIdsWritable: pendingPhoneNumberIds,
    };
  }),
  withMethods((store, api = inject(RetailerPhoneNumbersApi)) => {
    let loadRequestId = 0;

    const load = (force: boolean, retailerId: number): void => {
      if (!force && store.loadedRetailerId() === retailerId) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void api
        .load(force, retailerId)
        .then((records) => {
          if (requestId === loadRequestId) {
            patchState(store, { records, loadedRetailerId: retailerId });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, {
              records: [],
              errorMessage: extractErrorMessage(error, 'Unable to load retailer phone numbers.'),
            });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    return {
      load,
      markPendingPhoneNumbers(retailerPhoneNumberIds: number[]): void {
        if (retailerPhoneNumberIds.length === 0) {
          return;
        }

        const pendingIds = new Set(store.pendingPhoneNumberIdsWritable());
        for (const retailerPhoneNumberId of retailerPhoneNumberIds) {
          pendingIds.add(retailerPhoneNumberId);
        }

        store.pendingPhoneNumberIdsWritable.set(Array.from(pendingIds));
      },
      clearPendingPhoneNumbers(retailerPhoneNumberIds?: number[]): void {
        if (!retailerPhoneNumberIds || retailerPhoneNumberIds.length === 0) {
          store.pendingPhoneNumberIdsWritable.set([]);
          return;
        }

        const pendingIds = new Set(store.pendingPhoneNumberIdsWritable());
        for (const retailerPhoneNumberId of retailerPhoneNumberIds) {
          pendingIds.delete(retailerPhoneNumberId);
        }

        store.pendingPhoneNumberIdsWritable.set(Array.from(pendingIds));
      },
    };
  })
);
